import React, { useState, useCallback, useEffect } from "react";
import { Button, Modal } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import {
    UserProps,
    AuthReducerProps,
} from "../../../../store/reducers/authReducer";
import * as actions from "../../../../store/actions/index";
import api from "../../../../api/api";

import { validateEmail } from "../../../../utilities/validateEmail";
import Input from "../../../MUI/Input";
import SaveButton, { saveStatuses, saveTypes } from "../../../MUI/SaveButton";

interface Props {
    open: boolean;
    closePopup: () => void;
}

interface ErrorData {
    email: boolean;
    password: boolean;
    webRequest: string;
}

const AccountLogin: React.FC<Props> = ({ open, closePopup }) => {
    const [status, setStatus] = useState(saveStatuses.NEUTRAL);
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [resendMessage, setResendMessage] = useState<string>("");
    const [resendFailed, setResendFailed] = useState<boolean>(false);
    const [resendRequested, setResendRequested] = useState<boolean>(false);
    const [error, setError] = useState<ErrorData>({
        email: false,
        password: false,
        webRequest: "",
    });

    const history = useHistory();
    const dispatch = useDispatch();

    const me: UserProps | null = useSelector(
        (state: AuthReducerProps) => state.me,
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && status === saveStatuses.NEUTRAL) {
                setStatus(saveStatuses.SAVE_TRIGGERED);
            }
        },
        [status],
    );

    const handlePasswordLostClicked = useCallback(() => {
        closePopup();
        history.push("/home/recoverPassword");
    }, [history, closePopup]);

    const handleResendVerificationClicked = useCallback(() => {
        if (!validateEmail(email)) {
            setError((currentError) => {
                return {
                    ...currentError,
                    email: true,
                    webRequest: "",
                };
            });
            return;
        }

        setError((currentError) => {
            return {
                ...currentError,
                webRequest: "",
            };
        });
        setResendRequested(true);
        setResendFailed(false);
        setResendMessage("");

        api.post("/account/resendVerificationEmail", { email })
            .then((response) => {
                setResendFailed(false);
                setResendMessage(
                    response?.data?.message
                        ? response.data.message
                        : "If your account exists and is not verified yet, a new verification email will be sent shortly.",
                );
            })
            .catch(() => {
                setResendFailed(true);
                setResendMessage(
                    "Unable to request a new verification email right now. Please try again later.",
                );
            });
    }, [email]);

    useEffect(() => {
        if (me) {
            closePopup();
        }
    }, [me, closePopup]);

    useEffect(() => {
        if (status === saveStatuses.SAVE_TRIGGERED) {
            // Set to saving
            setStatus(saveStatuses.SAVING);

            // Initialise error data
            let errorData = {
                email: false,
                password: false,
                webRequest: "",
            };

            // Verify email address
            if (!validateEmail(email)) {
                errorData.email = true;
            }

            // Verify password
            if (!password || (password && password.trim() === "")) {
                errorData.password = true;
            }

            // Verify form errors
            setError(errorData);
            if (errorData.email || errorData.password) {
                setStatus(saveStatuses.ERROR);
                return;
            }

            setResendFailed(false);
            setResendMessage("");

            // Try to login
            api.post("/account/authenticate", { email, password })
                .then((response) => {
                    dispatch(actions.authLogin(response.data));
                    closePopup();
                })
                .catch((error) => {
                    setError({
                        email: false,
                        password: false,
                        webRequest: error?.response?.data?.message
                            ? error.response.data.message
                            : "Unable to log in right now. Please try again later.",
                    });
                    setStatus(saveStatuses.ERROR);
                });
        }
    }, [me, closePopup, email, password, status, dispatch]);

    const isInvalidCredentials =
        error.webRequest === "Email or password is incorrect";
    const lowerWebRequest = error.webRequest.toLowerCase();
    const isUnverifiedAccount =
        lowerWebRequest.includes("not verified") ||
        lowerWebRequest.includes("verification email");

    return (
        <Modal
            open={open}
            onClose={status === saveStatuses.NEUTRAL ? closePopup : () => {}}
            aria-labelledby="login-popup"
            aria-describedby="log into your account"
            className="d-flex align-items-center justify-content-center"
        >
            <div className="bg-white outline-0 oberin-popup">
                <div className="d-flex py-1 px-2 bg-dark-red text-white">
                    <span>Log into your account</span>
                    <div className="d-flex ml-auto align-items-center justify-content-center">
                        {status === saveStatuses.NEUTRAL ? (
                            <i
                                className="fas fa-times text-white clickable"
                                onClick={closePopup}
                            ></i>
                        ) : null}
                    </div>
                </div>
                <div className="d-flex flex-column p-2">
                    <Input
                        id="login-popup-email"
                        label="Email"
                        className="mt-2"
                        value={email}
                        error={error.email}
                        helperText={
                            error.email
                                ? "Please fill in a correct email"
                                : undefined
                        }
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => setEmail(event.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <Input
                        id="login-popup-password"
                        label="Password"
                        className="mt-2"
                        value={password}
                        error={error.password}
                        helperText={
                            error.password
                                ? "Please fill in a password"
                                : undefined
                        }
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => setPassword(event.target.value)}
                        onKeyDown={handleKeyDown}
                        type="password"
                    />

                    {error.webRequest && error.webRequest.trim() !== "" ? (
                        <div className="mt-2 mb-0 alert alert-danger rounded-0">
                            {error.webRequest}
                        </div>
                    ) : null}

                    {resendMessage.trim() !== "" ? (
                        <div
                            className={`mt-2 mb-0 alert rounded-0 ${resendFailed ? "alert-danger" : "alert-info"}`}
                        >
                            {resendMessage}
                        </div>
                    ) : null}

                    <div className="d-flex w-100 mt-2">
                        {!isInvalidCredentials ? null : (
                            <div className="w-50 mr-auto pr-2">
                                <Button
                                    key="btn-account-recover-password"
                                    variant="contained"
                                    className="w-100 dark-red-button rounded-0"
                                    onClick={handlePasswordLostClicked}
                                >
                                    Recover Password
                                </Button>
                            </div>
                        )}

                        {!isUnverifiedAccount || resendRequested ? null : (
                            <div className="w-50 mr-auto pr-2">
                                <Button
                                    key="btn-account-resend-verification"
                                    variant="contained"
                                    className="w-100 dark-red-button rounded-0"
                                    onClick={handleResendVerificationClicked}
                                >
                                    Request Verification Email
                                </Button>
                            </div>
                        )}

                        <div className="w-50 ml-auto pl-2">
                            <SaveButton
                                type={saveTypes.CONTROLLED}
                                status={status}
                                setStatus={setStatus}
                                showFeedback
                                hideCancel
                                saveClasses={["dark-red-button"]}
                                saveContent="Login"
                                savingContent={
                                    <>
                                        <i className="fas fa-sync fa-spin mr-2" />{" "}
                                        Logging in...
                                    </>
                                }
                                errorContent={
                                    <>
                                        <i className="fas fa-times mr-2" />{" "}
                                        Error
                                    </>
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AccountLogin;
